import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import {
  AspectRatioWrapper,
  IconArrowHead,
  ListingImageGallery,
  ResponsiveImage,
} from '../../components';
import classNames from 'classnames';
import FullscreenImages from '../TransactionPage/FullscreenImages/FullscreenImages';
import FullscreenButton from '../../components/FullscreenButton/FullscreenButton';

import css from './ListingPage.module.css';

const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const Swiper =
  typeof window !== 'undefined'
    ? require('swiper/react').Swiper
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });
const SwiperSlide =
  typeof window !== 'undefined'
    ? require('swiper/react').SwiperSlide
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });
const Pagination =
  typeof window !== 'undefined'
    ? require('swiper/modules').Pagination
    : dynamic(() => import('swiper/modules'), {
        ssr: false,
      });
const Autoplay =
  typeof window !== 'undefined'
    ? require('swiper/modules').Autoplay
    : dynamic(() => import('swiper/modules'), {
        ssr: false,
      });
const Navigation =
  typeof window !== 'undefined'
    ? require('swiper/modules').Navigation
    : dynamic(() => import('swiper/modules'), {
        ssr: false,
      });

const SectionGallery = props => {
  const {
    listing,
    variantPrefix,
    intl,
    config,
    platformClassName,
    onManageDisableScrolling,
  } = props;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const images = listing && listing.images ? listing.images : [];
  // const imageVariants = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];
  const imageVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];

  useEffect(() => {
    // Load swiper CSS only in the browser
    if (typeof window !== 'undefined') {
      require('swiper/css');
      require('swiper/css/bundle');
    }
  }, []);

  const storeImagesOrder = listing?.attributes?.publicData?.storeImagesOrder || [];
  const orderedImages = [...images].sort((a, b) => {
    const aIndex = storeImagesOrder?.indexOf(a.id?.uuid);
    const bIndex = storeImagesOrder?.indexOf(b.id?.uuid);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(orderedImages?.[0], imageVariants[0]);

  const onFullscreenOpen = () => {
    setIsFullscreen(true);
    galleryRef.current.fullScreen();
  };

  const galleryRef = useRef();

  const formattedImages = orderedImages?.map(image => {
    const originalImage = image?.attributes?.variants?.['listing-card-4x'];
    const thumbnailImage = image?.attributes?.variants?.['listing-card'];

    return {
      original: originalImage?.url,
      thumbnail: thumbnailImage?.url,
      name: originalImage?.name,
    };
  });

  return (
    <div className={classNames(css.productGallery, platformClassName)}>
    <button className={css.navLeft}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>

      <button className={css.navRight}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>

      <FullscreenButton onClick={onFullscreenOpen} />

      {orderedImages && (
        <Swiper
          className={css.swiperForListings}
          navigation={{
            nextEl: `.${css.navRight}`,
            prevEl: `.${css.navLeft}`,
          }}
          modules={[Pagination, Navigation, Autoplay]}
          autoplay={{
            delay: 6000,
            disableOnInteraction: false,
          }}
          loop={true}
          spaceBetween={10}
          // autoHeight={false}
          // slidesPerView={2.2}
          breakpoints={{
            425: {
              slidesPerView: 2.2,
            },
            0: {
              slidesPerView: 1,
            },
          }}
        >
          {orderedImages?.map((l, index) => (
            <SwiperSlide key={l.id.uuid}>
              <AspectRatioWrapper
                width={4}
                height={3}
                className={css.itemWrapper}
                customAspectBox={css.customAspectBox}
              >
                <ResponsiveImage
                  rootClassName={css.item}
                  image={l}
                  showAsBackground
                  alt={intl.formatMessage(
                    { id: 'ListingImageGallery.imageAltText' },
                    { index: index + 1, count: orderedImages.length }
                  )}
                  variants={imageVariants}
                />
              </AspectRatioWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <FullscreenImages
        id="ShopImagesId"
        images={formattedImages}
        alreadyFormatted
        disableDownload
        isModalOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        thumbnailClassName={css.thumb}
        rootGalleryClassName={css.rootGallery}
        wrapperFullscreen={css.wrapperFullscreen}
      />
    </div>
  );
};

export default SectionGallery;
